import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Vulnerability } from "@/types/vulnerability";
import { ExternalLink, AlertTriangle, Calendar, Clock, Shield, Database, Loader2, Monitor, Copy, Check } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { AppleDeviceIcon } from "@/components/icons";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNvdCve } from "@/hooks/useNvdEnrichment";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface VulnerabilityDetailPanelProps {
  vulnerability: Vulnerability | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VulnerabilityDetailPanel({ vulnerability, open, onOpenChange }: VulnerabilityDetailPanelProps) {
  const { data: nvdData, isLoading: nvdLoading } = useNvdCve(vulnerability?.cve_id ?? null);
  const [copied, setCopied] = useState(false);

  if (!vulnerability) return null;

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'bg-destructive text-destructive-foreground';
      case 'HIGH':
        return 'bg-[hsl(30_80%_55%)] text-white';
      case 'MEDIUM':
        return 'bg-[hsl(45_90%_55%)] text-black';
      case 'LOW':
        return 'bg-[hsl(220_70%_50%)] text-white';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const displaySeverity = nvdData?.severity || vulnerability.severity;
  const displayScore = nvdData?.cvssScore ?? vulnerability.cvss_score;
  const displayDescription = nvdData?.description || vulnerability.description;
  const publishedDate = nvdData?.publishedDate || vulnerability.published_date;
  const impactedDevices = vulnerability.impacted_devices || [];

  const daysAgo = Math.floor(
    (new Date().getTime() - new Date(publishedDate).getTime()) / (1000 * 60 * 60 * 24)
  );

  const generateClientSummary = () => {
    const update = vulnerability.affected_updates[0];
    const severityLabel = displaySeverity.toLowerCase();
    const exploitedText = vulnerability.actively_exploited
      ? " This vulnerability is being actively exploited by attackers in the wild, making it especially urgent to address."
      : "";
    const deviceText = impactedDevices.length > 0
      ? ` ${impactedDevices.length} device${impactedDevices.length !== 1 ? "s" : ""} in your environment ${impactedDevices.length !== 1 ? "are" : "is"} currently affected.`
      : " No devices in your environment are currently affected.";
    const actionText = update
      ? ` The fix is available in ${update.name} (version ${update.version}). We recommend applying this update as soon as possible.`
      : "";

    return `Security Advisory: ${vulnerability.cve_id}\n\nA ${severityLabel}-severity vulnerability (${vulnerability.cve_id}) has been identified in ${vulnerability.os_types.join(", ")}.${exploitedText}${deviceText}${actionText}\n\nCVSS Score: ${displayScore !== null ? `${displayScore}/10` : "N/A"}\nPublished: ${format(new Date(publishedDate), "MMMM d, yyyy")}`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generateClientSummary());
      setCopied(true);
      toast.success("Summary copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <SheetTitle className="text-2xl font-mono">{vulnerability.cve_id}</SheetTitle>
              <a
                href={`https://nvd.nist.gov/vuln/detail/${vulnerability.cve_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:underline flex items-center gap-1 mt-1"
              >
                View on NVD <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            {nvdData && (
              <Badge variant="outline" className="text-xs gap-1">
                <Database className="h-3 w-3" />
                NVD Enriched
              </Badge>
            )}
            {nvdLoading && (
              <Badge variant="outline" className="text-xs gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading NVD…
              </Badge>
            )}
          </div>
        </SheetHeader>

        {/* Description */}
        <div className="mt-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {displayDescription || "No description available."}
          </p>
        </div>

        {/* Quick Metrics */}
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              Severity
            </div>
            <Badge className={`${getSeverityColor(displaySeverity)} text-base`}>
              {displaySeverity}
            </Badge>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              CVSS Score
            </div>
            <div className="text-2xl font-bold">
              {displayScore !== null ? (
                <>
                  {displayScore.toFixed(1)}<span className="text-sm text-muted-foreground">/10</span>
                </>
              ) : (
                <span className="text-sm text-muted-foreground">N/A</span>
              )}
            </div>
            {nvdData?.cvssVersion && (
              <span className="text-xs text-muted-foreground">CVSS v{nvdData.cvssVersion}</span>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4" />
              Known Exploit
            </div>
            <div className="text-base font-semibold">
              {vulnerability.actively_exploited ? (
                <span className="text-[hsl(30_80%_55%)]">Yes - Active</span>
              ) : (
                <span className="text-muted-foreground">No</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Age
            </div>
            <div className="text-base font-semibold">{daysAgo} days</div>
          </div>
        </div>

        {/* CVSS Vector */}
        {nvdData?.cvssVector && (
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <div className="text-xs font-medium text-muted-foreground mb-1">CVSS Vector</div>
            <code className="text-xs font-mono break-all">{nvdData.cvssVector}</code>
          </div>
        )}

        {/* Weaknesses */}
        {nvdData?.weaknesses && nvdData.weaknesses.length > 0 && (
          <div className="mt-4">
            <div className="text-sm font-medium mb-2">Weaknesses (CWE)</div>
            <div className="flex flex-wrap gap-2">
              {nvdData.weaknesses.map((cwe, i) => (
                <Badge key={i} variant="secondary" className="text-xs font-mono">{cwe}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Timeline</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Published</span>
              <span className="font-medium">{format(new Date(publishedDate), 'MMM d, yyyy')}</span>
            </div>
            {nvdData?.lastModifiedDate && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Last Modified</span>
                <span className="font-medium">{format(new Date(nvdData.lastModifiedDate), 'MMM d, yyyy')}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Last Detected</span>
              <span className="font-medium">{formatDistanceToNow(new Date(publishedDate), { addSuffix: true })}</span>
            </div>
          </div>
        </div>

        {/* Tabbed Content */}
        <Tabs defaultValue="updates" className="mt-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="updates">Affected Updates</TabsTrigger>
            <TabsTrigger value="references">References</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
          </TabsList>

          <TabsContent value="updates" className="space-y-3 mt-4">
            <ScrollArea className="h-[300px] pr-4">
              {vulnerability.affected_updates.map((update, idx) => (
                <div
                  key={idx}
                  className="p-4 border rounded-lg mb-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <AppleDeviceIcon osType={update.os_type} className="h-5 w-5 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium">{update.name}</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Version {update.version} • Released {format(new Date(update.released), 'MMM d, yyyy')}
                        </div>
                      </div>
                    </div>
                    <a
                      href={update.notes_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="references" className="space-y-3 mt-4">
            <ScrollArea className="h-[300px] pr-4">
              {nvdData?.references && nvdData.references.length > 0 ? (
                nvdData.references.map((ref, idx) => (
                  <a
                    key={idx}
                    href={ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 border rounded-lg mb-2 hover:bg-muted/50 transition-colors text-sm"
                  >
                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate text-primary hover:underline">{ref.url}</span>
                    {ref.source && (
                      <Badge variant="outline" className="text-xs ml-auto shrink-0">{ref.source}</Badge>
                    )}
                  </a>
                ))
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  {nvdLoading ? "Loading references from NVD…" : "No references available."}
                </p>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="insights" className="space-y-4 mt-4">
            <div className="space-y-3">
              {/* Affected Devices */}
              {impactedDevices.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Monitor className="h-4 w-4" />
                    Affected Devices
                    <Badge variant="destructive" className="text-xs">{impactedDevices.length}</Badge>
                  </h4>
                  <ScrollArea className="max-h-[180px]">
                    <div className="space-y-1.5">
                      {impactedDevices.map((device) => (
                        <div
                          key={device.id}
                          className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-muted/50"
                        >
                          <div className="flex items-center gap-2">
                            <Monitor className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-medium">{device.hostname || device.name || "Unknown"}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {device.serial_number && <span>{device.serial_number}</span>}
                            <Badge variant="outline" className="text-xs">{device.os_version || "Unknown"}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              <div>
                <h4 className="font-semibold mb-2">Patch Availability</h4>
                <p className="text-sm text-muted-foreground">
                  {vulnerability.affected_updates.length} update{vulnerability.affected_updates.length !== 1 ? 's' : ''} available that address this vulnerability.
                  {vulnerability.affected_updates[0] && (
                    <> Update to <strong className="text-foreground">{vulnerability.affected_updates[0].name} ({vulnerability.affected_updates[0].version})</strong> to remediate.</>
                  )}
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Risk Assessment</h4>
                <p className="text-sm text-muted-foreground">
                  {displaySeverity === 'CRITICAL' && 
                    "This is a critical vulnerability that should be addressed immediately. It poses a significant security risk."
                  }
                  {displaySeverity === 'HIGH' && 
                    "This is a high severity vulnerability that should be prioritized for patching."
                  }
                  {displaySeverity === 'MEDIUM' && 
                    "This is a medium severity vulnerability that should be addressed in your regular patching cycle."
                  }
                  {displaySeverity === 'LOW' && 
                    "This is a low severity vulnerability. Monitor and patch during routine maintenance."
                  }
                  {impactedDevices.length > 0 && (
                    <> Currently {impactedDevices.length} device{impactedDevices.length !== 1 ? "s" : ""} in your fleet {impactedDevices.length !== 1 ? "are" : "is"} running a vulnerable version.</>
                  )}
                </p>
              </div>

              {vulnerability.actively_exploited && (
                <div className="p-4 bg-[hsl(30_80%_55%)]/10 border border-[hsl(30_80%_55%)]/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-[hsl(30_80%_55%)] mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-[hsl(30_80%_55%)]">Actively Exploited</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        This vulnerability is being actively exploited in the wild. Immediate patching is strongly recommended.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <h4 className="font-semibold mb-2">Remediation Steps</h4>
                <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                  {vulnerability.affected_updates[0] ? (
                    <>
                      <li>Deploy <strong className="text-foreground">{vulnerability.affected_updates[0].name}</strong> to all affected devices</li>
                      <li>Verify successful installation across {impactedDevices.length > 0 ? `${impactedDevices.length} affected device${impactedDevices.length !== 1 ? "s" : ""}` : "your fleet"}</li>
                      <li>Monitor for any post-update issues and confirm remediation</li>
                    </>
                  ) : (
                    <>
                      <li>Review the affected updates and determine applicability to your devices</li>
                      <li>Test the updates in a non-production environment</li>
                      <li>Deploy updates to affected devices through your patch management policy</li>
                      <li>Verify successful installation and monitor for issues</li>
                    </>
                  )}
                </ol>
              </div>

              {/* Copy Summary Button */}
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2 gap-2"
                onClick={handleCopy}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied!" : "Copy summary for client"}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
